import React, { useState } from 'react';
import { Box, Flex, Heading, Text } from 'rebass';

const DiffChecker = ({ night, setNight }) => {
  const [leftText, setLeftText] = useState('');
  const [rightText, setRightText] = useState('');

  // Simple line-by-line diff algorithm
  const computeDiff = (left, right) => {
    const leftLines = left.split('\n');
    const rightLines = right.split('\n');
    const maxLines = Math.max(leftLines.length, rightLines.length);
    
    const diff = [];
    
    for (let i = 0; i < maxLines; i++) {
      const leftLine = leftLines[i] || '';
      const rightLine = rightLines[i] || '';
      
      let status = 'unchanged';
      if (i >= leftLines.length) {
        status = 'added';
      } else if (i >= rightLines.length) {
        status = 'removed';
      } else if (leftLine !== rightLine) {
        status = 'modified';
      }
      
      diff.push({
        lineNumber: i + 1,
        left: leftLine,
        right: rightLine,
        status
      });
    }
    
    return diff;
  };

  const diff = computeDiff(leftText, rightText);

  const getLineStyle = (status) => {
    const baseStyle = {
      fontFamily: 'Menlo, ui-monospace, monospace',
      fontSize: 1,
      p: 1,
      borderLeft: '3px solid',
      minHeight: '20px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all'
    };

    switch (status) {
      case 'added':
        return {
          ...baseStyle,
          bg: '#d4edda',
          borderColor: '#28a745',
          color: '#155724'
        };
      case 'removed':
        return {
          ...baseStyle,
          bg: '#f8d7da',
          borderColor: '#dc3545',
          color: '#721c24'
        };
      case 'modified':
        return {
          ...baseStyle,
          bg: '#fff3cd',
          borderColor: '#ffc107',
          color: '#856404'
        };
      default:
        return {
          ...baseStyle,
          bg: 'transparent',
          borderColor: '#e9ecef',
          color: 'text'
        };
    }
  };

  return (
    <Box maxWidth="1200px" mx="auto" mt={4} mb={4} p={4}>
      <Heading as="h1" fontSize={4} mb={3} textAlign="center" sx={{ fontFamily: "Menlo, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', 'Courier New', monospace" }}>
        Text Diff Checker
      </Heading>
      <Text fontSize={2} color="gray" mb={4} textAlign="center">
        Compare two texts line by line. Paste your content in the areas below to see the differences.
      </Text>

      {/* Input Section */}
      <Flex flexDirection={['column', 'column', 'row']} mb={4} sx={{ gap: 3 }}>
        <Box flex="1">
          <Text fontWeight="bold" mb={2}>
            Original Text:
          </Text>
          <Box
            as="textarea"
            width="100%"
            rows={12}
            value={leftText}
            onChange={(e) => setLeftText(e.target.value)}
            sx={{
              fontFamily: 'Menlo, ui-monospace, monospace',
              fontSize: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid #ccc',
              resize: 'vertical'
            }}
            placeholder="Paste your original text here..."
          />
        </Box>
        
        <Box flex="1">
          <Text fontWeight="bold" mb={2}>
            Comparison Text:
          </Text>
          <Box
            as="textarea"
            width="100%"
            rows={12}
            value={rightText}
            onChange={(e) => setRightText(e.target.value)}
            sx={{
              fontFamily: 'Menlo, ui-monospace, monospace',
              fontSize: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid #ccc',
              resize: 'vertical'
            }}
            placeholder="Paste your comparison text here..."
          />
        </Box>
      </Flex>

      {/* Diff Results */}
      {(leftText || rightText) && (
        <Box>
          <Heading as="h3" fontSize={3} mb={3} sx={{ fontFamily: "Menlo, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', 'Courier New', monospace" }}>
            Diff Results:
          </Heading>
          
          {/* Legend */}
          <Flex mb={3} flexWrap="wrap" sx={{ gap: 2 }}>
            <Flex alignItems="center">
              <Box width="16px" height="16px" bg="#28a745" mr={1} />
              <Text fontSize={1}>Added</Text>
            </Flex>
            <Flex alignItems="center">
              <Box width="16px" height="16px" bg="#dc3545" mr={1} />
              <Text fontSize={1}>Removed</Text>
            </Flex>
            <Flex alignItems="center">
              <Box width="16px" height="16px" bg="#ffc107" mr={1} />
              <Text fontSize={1}>Modified</Text>
            </Flex>
            <Flex alignItems="center">
              <Box width="16px" height="16px" bg="#e9ecef" mr={1} />
              <Text fontSize={1}>Unchanged</Text>
            </Flex>
          </Flex>

          {/* Diff Display */}
          <Flex flexDirection={['column', 'column', 'row']} sx={{ gap: 2 }}>
            <Box flex="1">
              <Text fontWeight="bold" mb={2} fontSize={2}>
                Original (Lines: {leftText.split('\n').length})
              </Text>
              <Box sx={{ border: '1px solid #ddd', borderRadius: 2 }}>
                {diff.map((line, index) => (
                  <Flex key={index} sx={{ borderBottom: index < diff.length - 1 ? '1px solid #eee' : 'none' }}>
                    <Box 
                      sx={{ 
                        bg: '#f8f9fa', 
                        px: 2, 
                        py: 1, 
                        borderRight: '1px solid #eee', 
                        minWidth: '40px',
                        textAlign: 'right',
                        fontFamily: 'Menlo, ui-monospace, monospace',
                        fontSize: 0,
                        color: 'gray'
                      }}
                    >
                      {line.left !== '' ? line.lineNumber : ''}
                    </Box>
                    <Box flex="1" sx={getLineStyle(line.status === 'added' ? 'removed' : line.status)}>
                      {line.left || '\n'}
                    </Box>
                  </Flex>
                ))}
              </Box>
            </Box>

            <Box flex="1">
              <Text fontWeight="bold" mb={2} fontSize={2}>
                Comparison (Lines: {rightText.split('\n').length})
              </Text>
              <Box sx={{ border: '1px solid #ddd', borderRadius: 2 }}>
                {diff.map((line, index) => (
                  <Flex key={index} sx={{ borderBottom: index < diff.length - 1 ? '1px solid #eee' : 'none' }}>
                    <Box 
                      sx={{ 
                        bg: '#f8f9fa', 
                        px: 2, 
                        py: 1, 
                        borderRight: '1px solid #eee', 
                        minWidth: '40px',
                        textAlign: 'right',
                        fontFamily: 'Menlo, ui-monospace, monospace',
                        fontSize: 0,
                        color: 'gray'
                      }}
                    >
                      {line.right !== '' ? line.lineNumber : ''}
                    </Box>
                    <Box flex="1" sx={getLineStyle(line.status === 'removed' ? 'added' : line.status)}>
                      {line.right || '\n'}
                    </Box>
                  </Flex>
                ))}
              </Box>
            </Box>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default DiffChecker;
